import React, {useCallback, useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import {useRecoilValue, useSetRecoilState} from "recoil";
import ReCAPTCHA from 'react-google-recaptcha';

// Components
import ShortText from "sharedComponents/FormQuestions/components/ShortText";
import LongText from "sharedComponents/FormQuestions/components/LongText";
import Grading from "sharedComponents/FormQuestions/components/Grading";
import Rating from "sharedComponents/FormQuestions/components/Rating";
import ENPS from "sharedComponents/FormQuestions/components/ENPS";
import SingleOrMultiChoice from "sharedComponents/FormQuestions/components/SingleOrMultiChoice";
import Range from "sharedComponents/FormQuestions/components/Range";
import EmptyListMessage from "sharedComponents/EmptyListMessage/EmptyListMessage";

// Utils
import {fieldTypeOptions} from "@forms/components/SpecificForm/AddNewFieldModal/addNewFieldModal.config";
import {emptyFormFields} from "sharedComponents/EmptyListMessage/message.config";
import {externalSurvey} from "models/externalSurvey";
import {getSurveyCompletionStatuses, surveysDataPaginated, surveyStatuses} from "store";
import note from "Hooks/useNotification";

// Constants
import {overallPerformanceRating} from "sharedComponents/FormQuestions/config";
import {validationLength} from "constants/validationLength";
import UserDemographicInfo from "./components/UserDemographicInfo";

// Hooks
import useDeviceType from "context/deviceType";

// Styles
import "./style/user_survey_questions.scss";

const UserSurveyQuestions = ({survey, formData, surveyId, handleChangeSurveyCompletionStatus, mandatoryRefs}) => {
    const {isMobile, isDesktop} = useDeviceType();
    const { t } = useTranslation();
    const [answers, SetAnswers] = useState({});
    const [toGetAnswers, SetAnswersStatus] = useState(false);
    const [loading, SetLoading] = useState(false);
    const [isAnonymous, SetIsAnonymous] = useState(false);
    const setSurveysData = useSetRecoilState(surveysDataPaginated);
    const setSurveyStatuses = useSetRecoilState(surveyStatuses);
    const completionStatus = useRecoilValue(getSurveyCompletionStatuses);
    const host = window.location.hostname;
    const subdomain = host.split('.')[0];
    const domains = ['localhost', '2leapappdev', '2leapapp'];
    const [demographicInfo, setDemographicInfo] = useState([]);
    const [demographicErrors, setDemographicErrors] = useState({});
    const [captchaToken, setCaptchaToken] = useState(null);
    const [captchaError, setCaptchaError] = useState('');

    useEffect(() => {
        toGetAnswers && SetAnswersStatus(false);
    }, [toGetAnswers]);

    const handleGetAnswers = (anonymous) => {
        if (loading) {
            return;
        }

        SetIsAnonymous(anonymous);
        SetAnswersStatus(true);
    };

    useEffect(() => {
        if (!!Object.keys(answers).length) {
            if (!captchaToken) {
                setCaptchaError('Please complete the CAPTCHA');
                return;
            }
            const demographicErrors = {};
            const answerMap = {};
            const mandatoryFields = formData && JSON.parse(JSON.stringify(formData))?.fields;

            const validatingCond = mandatoryFields?.find((item) => {
                const answer = answers[item.id]?.answer;
                const explanation = answers[item.id]?.explanation?.trim();

                if (item.field_type === 'Long text' || item.field_type === 'Short text') {
                    const length = item.field_type === 'Short text' ? validationLength.medium : validationLength.large;

                    if (answer?.trim()?.length > length) {
                        return true;
                    }
                }

                if (item.explanation && (item.field_type === 'Range' || item.field_type === 'Grading' || item.field_type === 'Multi-choice group' || item.field_type === 'Single choice group')) {
                    const isOverall = item.label?.includes(overallPerformanceRating);
                    if ((isOverall && !explanation?.length) || explanation?.length > 256) {
                        return true;
                    }
                }

                if (item.mandatory && ((item.field_type === 'eNPS') && ((answer !== 0) && !answer))) {
                    return true;
                } else if (item.mandatory && (item.field_type !== 'eNPS') && !answer) {
                    return true;
                }
            });

            demographicInfo.forEach((info) => {
                answerMap[info.demographic_information_id] = info.answer?.trim();
            });
            survey.demographic_informations.forEach((info) => {
                const value = answerMap[info.id];

                // Check required
                if (info.is_mandatory && !value) {
                    demographicErrors[info.id] = t("Required");
                    return;
                }

                // Email validation
                const isEmailField =
                    info?.option?.field_name === "email";

                if (isEmailField && value) {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                    if (!emailRegex.test(value)) {
                        demographicErrors[info.id] = t("Please enter a valid email address");
                    }
                }
            });

            setDemographicErrors(demographicErrors);

            if (Object.keys(demographicErrors).length > 0) {
                return;
            }

            if (document.cookie.includes(`survey_${survey.external_uuid}=true`)) {
                note(
                    "error",
                    t("You have already submitted this survey."),
                );
                return;
            }

            if (!validatingCond) {
                const results = {
                    field_answers: answers,
                    is_anonymous: Number(isAnonymous),
                    device_type: isDesktop ? 'desktop' : isMobile ? 'mobile' : 'tablet',
                    demographic_info: demographicInfo,
                    captcha_token: captchaToken,
                };

                SetLoading(true);

                externalSurvey.submitSurveyAnswer(surveyId, results).then((res) => {
                    if (!res?.data?.success) {
                        note(
                            "error",
                            t(res?.data?.message ?? "Something wen wrong"),
                        );

                        SetLoading(false);
                        return;
                    }

                    const expires = new Date();
                    expires.setFullYear(expires.getFullYear() + 1);
                    document.cookie = `survey_${survey.external_uuid}=true; expires=${expires.toUTCString()}; path=/`;

                    note(
                        "success",
                        t("Survey Response Submitted."),
                        t("Your survey response has been submitted.")
                    );

                    window.location.href = `${process.env.REACT_APP_DOMAIN}/es-pms/success/${survey.id}`;
                });
            }
        }
        // eslint-disable-next-line
    }, [
        answers,
        surveyId,
        formData,
        setSurveysData,
        setSurveyStatuses,
    ]);

    const getCurrentField = useCallback((field) => {
        switch (field.field_type) {
            case fieldTypeOptions[2]:
                return <ENPS
                    key={field.id}
                    fieldData={field}
                    getAnswer={!toGetAnswers ? toGetAnswers : SetAnswers}
                    handleChangeSurveyCompletionStatus={!completionStatus?.dropouts ? handleChangeSurveyCompletionStatus : null}
                    mandatoryRefs={mandatoryRefs}
                />
            case fieldTypeOptions[0]:
                return <Rating
                    key={field.id}
                    fieldData={field}
                    getAnswer={!toGetAnswers ? toGetAnswers : SetAnswers}
                    handleChangeSurveyCompletionStatus={!completionStatus?.dropouts ? handleChangeSurveyCompletionStatus : null}
                    mandatoryRefs={mandatoryRefs}
                />
            case fieldTypeOptions[6]:
                return <ShortText
                    key={field.id}
                    fieldData={field}
                    getAnswer={!toGetAnswers ? toGetAnswers : SetAnswers}
                    handleChangeSurveyCompletionStatus={!completionStatus?.dropouts ? handleChangeSurveyCompletionStatus : null}
                    mandatoryRefs={mandatoryRefs}
                />
            case fieldTypeOptions[7]:
                return <LongText
                    key={field.id}
                    fieldData={field}
                    getAnswer={!toGetAnswers ? toGetAnswers : SetAnswers}
                    handleChangeSurveyCompletionStatus={!completionStatus?.dropouts ? handleChangeSurveyCompletionStatus : null}
                    mandatoryRefs={mandatoryRefs}
                />
            case fieldTypeOptions[1]:
                return <Grading
                    key={field.id}
                    fieldData={field}
                    getAnswer={!toGetAnswers ? toGetAnswers : SetAnswers}
                    handleChangeSurveyCompletionStatus={!completionStatus?.dropouts ? handleChangeSurveyCompletionStatus : null}
                    mandatoryRefs={mandatoryRefs}
                />
            case fieldTypeOptions[4]:
            case fieldTypeOptions[5]:
                return <SingleOrMultiChoice
                    key={field.id}
                    type={field.field_type === fieldTypeOptions[5] ? 1 : 0}
                    fieldData={field}
                    getAnswer={!toGetAnswers ? toGetAnswers : SetAnswers}
                    handleChangeSurveyCompletionStatus={!completionStatus?.dropouts ? handleChangeSurveyCompletionStatus : null}
                    mandatoryRefs={mandatoryRefs}
                />
            case fieldTypeOptions[3]:
                return <Range
                    key={field.id}
                    fieldData={field}
                    getAnswer={!toGetAnswers ? toGetAnswers : SetAnswers}
                    handleChangeSurveyCompletionStatus={!completionStatus?.dropouts ? handleChangeSurveyCompletionStatus : null}
                    mandatoryRefs={mandatoryRefs}
                />
            default:
                return null;
        }
    }, [toGetAnswers]);

    useEffect(() => {
        if (mandatoryRefs.length) {
            mandatoryRefs = mandatoryRefs.filter(ref => ref.className.includes("invalid"));

            if (mandatoryRefs[0] && mandatoryRefs[0].scrollIntoView) {
                mandatoryRefs[0].scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    });

    return (
        <div className="user_survey_questions">
            {formData?.fields?.map(getCurrentField)}

            {formData?.fields?.length ? (
                <>
                    <UserDemographicInfo
                        survey={survey}
                        demographicInfo={demographicInfo}
                        setDemographicInfo={setDemographicInfo}
                        demographicErrors={demographicErrors}
                        mandatoryRefs={mandatoryRefs}
                    />
                    <div>
                        <div className="survey_recaptcha">
                            <ReCAPTCHA
                                sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
                                onChange={(token) => {
                                    setCaptchaToken(token);
                                    setCaptchaError('');
                                }
                                }
                            />
                            {
                                captchaError && (
                                    <p className="error_text">{captchaError}</p>
                                )
                            }
                        </div>
                    </div>
                    <div className="user_survey_questions_actions">
                        <button
                            type="button"
                            className="user_survey_questions_actions_primary submit_btn"
                            onClick={() => handleGetAnswers(false)}
                            disabled={loading}
                        >
                            {domains.includes(subdomain) ? t('Submit') : t('Submit answers')}
                        </button>
                    </div>
                </>
            ) : (
                <EmptyListMessage message={t(emptyFormFields)} />
            )}
        </div>
    );
};

export default React.memo(UserSurveyQuestions);
